import { track } from '@amplitude/analytics-browser';
import type { ColumnDef } from '@tanstack/react-table';
import commaNumber from 'comma-number';
import { HelpCircle, MoreVertical, Star } from 'lucide-react';
import { useMemo } from 'react';

import { Flex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Menu from '@/components/Menu/Menu';
import AMP_KEYS from '@/configs/amp-track-keys';
import { useFeedbackTerminology } from '@/entities/workspace/hooks';
import { cn } from '@/lib/cn';

import { useIssueListActions } from '../../../hooks/use-issue-list-query';
import {
  getPeriodDateRangeLabels,
  getPeriodLabel,
} from '../../../lib/signal-date-preset.utils';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '../../../model';
import { ChangesLabel } from '../../changes-label';
import { SignalBadge } from '../../signal-badge';

type Props = {
  dynamicFilterSet: StructuredFilter;
  datePreset: SignalDateSelectionInput;
};

export const useIssueListColumns = ({
  dynamicFilterSet,
  datePreset,
}: Props) => {
  const { hideIssue, favoriteIssue, navigateToIssueDetailPage } =
    useIssueListActions();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();

  const columns: ColumnDef<CompositedSignalIssue>[] = useMemo(
    () => [
      {
        id: '#',
        header: () => (
          <div className="flex items-center justify-center flex-1">
            <span>#</span>
          </div>
        ),
        cell: ({ row }) => (
          <p className="text-sm font-medium text-center w-full">
            {row.index + 1}
          </p>
        ),
        size: 56,
      },
      {
        id: 'issue',
        header: () => <span>Issue</span>,
        cell: ({ row }) => (
          <div className="flex items-center gap-2 w-full">
            <p className="min-w-0 wrap-break-word text-sm leading-[22px] font-medium text-gray-950">
              {row.original.name}
            </p>

            <Star
              className={cn(
                'size-4 shrink-0 cursor-pointer',
                row.original.favorite
                  ? 'text-yellow-500 fill-yellow-500'
                  : 'text-gray-400 fill-none',
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (!row.original.favorite) {
                  track(AMP_KEYS.SIGNAL_ISSUE_FAVORITE, {
                    issue_name: row.original.name,
                  });
                }
                favoriteIssue(row.original.name, !row.original.favorite);
              }}
            />
          </div>
        ),
        meta: { className: 'flex-1 px-2' },
      },
      {
        id: 'feedback',
        header: () => (
          <span>{replaceFeedbackTerminology('Feedback count')}</span>
        ),
        cell: ({ row }) => (
          <p className="text-sm leading-[22px] font-medium text-gray-950 text-center">
            {commaNumber(row.original.selectedPeriodCount)}
          </p>
        ),
        size: 120,
        meta: { className: 'px-2 justify-end' },
      },
      {
        id: 'changes',
        header: () => (
          <Flex a="center" g={0} className="justify-end w-full">
            <IconButton
              outlined={false}
              icon={<HelpCircle className="size-3.5 text-muted-foreground" />}
              sizeType="s"
              tooltip={
                <div className="flex flex-col gap-1.5 text-sm">
                  <p className="font-medium">
                    {replaceFeedbackTerminology('Feedback count comparison')}
                  </p>
                  <p className="text-xs opacity-80">
                    {replaceFeedbackTerminology(
                      'Change in feedback volume compared to the same period in the past.',
                    )}
                  </p>
                  <div className="flex flex-col gap-0.5 text-xs opacity-70">
                    <p className="flex">
                      <span className="w-[60px]">Previous:</span>
                      {getPeriodDateRangeLabels(datePreset).comparisonPeriod}
                    </p>
                    <p className="flex">
                      <span className="w-[60px]">Current:</span>
                      {getPeriodDateRangeLabels(datePreset).selectedPeriod}
                    </p>
                  </div>
                </div>
              }
            />
            <span>{getPeriodLabel(datePreset)} change</span>
          </Flex>
        ),
        cell: ({ row }) => {
          return (
            <ChangesLabel
              comparisonPeriodCount={row.original.comparisonPeriodCount}
              selectedPeriodCount={row.original.selectedPeriodCount}
              datePreset={datePreset}
            />
          );
        },
        size: 160,
        meta: { className: 'px-4 text-right justify-end' },
      },
      {
        id: 'signal',
        header: () => <span>Signals</span>,
        cell: ({ row }) => {
          const { signalTitles } = row.original;
          if (signalTitles.length === 0) return <span>-</span>;

          const firstName = signalTitles[0];
          const remainingCount = signalTitles.length - 1;

          return (
            <div className="flex items-center gap-1">
              <SignalBadge
                title={firstName}
                tooltip={signalTitles.join(', ')}
              />
              {remainingCount > 0 && (
                <span className="text-xs text-foreground font-medium whitespace-nowrap">
                  and {remainingCount}+
                </span>
              )}
            </div>
          );
        },
        size: 190,
        meta: { className: 'px-4' },
      },
      {
        id: 'actions',
        header: () => <span></span>,
        cell: ({ row }) => (
          <Menu
            triggerNode={
              <IconButton
                icon={<MoreVertical className="w-4 h-4" />}
                sizeType="m"
                outlined={false}
              />
            }
            options={[
              {
                label: 'View',
                onClick: () => {
                  track(AMP_KEYS.SIGNAL_ISSUE_DETAIL_VIEW, {
                    issue_name: row.original.name,
                    severity: row.original.severity,
                    view_type: 'menu',
                  });
                  navigateToIssueDetailPage(
                    row.original.name,
                    dynamicFilterSet,
                    datePreset,
                  );
                },
              },
              {
                label: row.original.favorite ? 'Unfavorite' : 'Favorite',
                onClick: async () => {
                  await favoriteIssue(
                    row.original.name,
                    !row.original.favorite,
                  );
                },
              },
              {
                label: 'Hide',
                onClick: async () => {
                  await hideIssue(row.original.name, true);
                },
              },
            ]}
          />
        ),
        size: 48,
        meta: { className: 'p-1' },
      },
    ],
    [
      datePreset,
      dynamicFilterSet,
      hideIssue,
      favoriteIssue,
      navigateToIssueDetailPage,
      replaceFeedbackTerminology,
    ],
  );

  return { columns };
};
